import React from 'react';

import type { MenuItem } from '@/shared/types';
import {
  isDynamicImportFailure,
  tryRecoverFromDynamicImportFailure,
} from '@/shared/utils/dynamicImportRecovery';
import { cn, type ThemeClasses } from '@/ui/theme/ThemeContext';
import { useSettings } from '@/features/settings/contexts/SettingsContext';

interface ViewErrorFallbackProps {
  activeMenuItem: MenuItem;
  error: Error;
  reset: () => void;
  theme: ThemeClasses;
}

export const ViewErrorFallback: React.FC<ViewErrorFallbackProps> = ({
  activeMenuItem,
  error,
  reset,
  theme,
}) => {
  const { t } = useSettings();
  const isImportFailure = isDynamicImportFailure(error);

  React.useEffect(() => {
    tryRecoverFromDynamicImportFailure(error);
  }, [error]);

  return (
    <div
      data-view-probe-state="error"
      className={cn(
        'flex items-center justify-center h-full p-8',
        theme.bgPrimary
      )}
    >
      <div className="text-center">
        <h3 className={cn('text-lg font-semibold mb-2', theme.textPrimary)}>
          {t('Error loading view')}
        </h3>
        <p className={cn('text-sm mb-4', theme.textSecondary)}>
          {isImportFailure
            ? t('Refreshing the {{view}} view because its local app bundle changed.', { view: t(activeMenuItem.label) })
            : t('Failed to load the {{view}} view.', { view: t(activeMenuItem.label) })}
        </p>
        <div className="flex justify-center gap-2">
          <button
            onClick={reset}
            className={cn(
              'px-4 py-2 rounded-md border',
              theme.borderPrimary,
              theme.textPrimary,
              theme.surfaceHover
            )}
          >
            {t('Try Again')}
          </button>
          <button
            onClick={() => window.location.reload()}
            className={cn(
              'px-4 py-2 rounded-md text-white',
              theme.accentBlue.split(' ')[1]
            )}
          >
            {t('Reload Page')}
          </button>
        </div>
      </div>
    </div>
  );
};
